<!-- App.vue -->

<!-- HTML Template -->
<template>
    <div id="upload">
        <div v-if="isLoading">
            <centered-spinner />
        </div>
        <b-container v-else class="container">
            <b-row>
                <b-col>
                    <b-form
                        @submit="upload"
                        enctype="multipart/form-data"
                        novalidate
                        v-if="isInitial || isSaving"
                    >
                        <div>
                            <div v-if="!isSaving">
                                <b-form-group>
                                    <h4>Image or Audio File</h4>
                                    <div style="font-size: 17px; margin: 5px 0 -5px 0">
                                        You have used
                                        <b>{{ count.numUploads }}</b> out of
                                        <b>{{ count.maxUploads }}</b> uploads.
                                        <b-btn
                                            v-if="!user.isSubscribed && !user.trialing"
                                            style="margin-left: 5px"
                                            v-b-tooltip="'Subscribing increases the limit to 100'"
                                            variant="cliovis-blue-light"
                                            size="sm"
                                            @click="$refs.subscriptionModal.show()"
                                            pill
                                        >
                                            <i class="fas fa-plus" /> Increase Limit
                                        </b-btn>
                                    </div>
                                </b-form-group>
                                <b-form-group id="nameGroup" label="Name">
                                    <b-form-input
                                        :state="!$v.form.filename.$invalid"
                                        v-model="form.filename"
                                        placeholder="File Name"
                                        autofocus
                                    />
                                </b-form-group>
                                <b-form-group>
                                    <b-form-textarea
                                        :state="!$v.form.filedescription.$invalid"
                                        v-model="form.filedescription"
                                        placeholder="File Description"
                                    />
                                </b-form-group>
                                <form ref="uploadForm" class="upload-box">
                                    <input
                                        ref="uploadInputElement"
                                        type="file"
                                        id="file"
                                        class="upload-input"
                                        :state="!$v.form.file.$invalid"
                                        v-on:change="onChange($event)"
                                        placeholder="Drag A File Here"
                                        :disabled="(isSaving)"
                                        accept=".jpg, .png, .jpeg, .gif, .mp3, .wav, .ogg, .m4a"
                                    />
                                    <label
                                        class="dragndrop-label"
                                        ref="uploadInputBox"
                                        draggable
                                        for="file"
                                    >
                                        <span v-if="form.file">{{ form.file.name }}</span>
                                        <span v-else>
                                            <strong>Click to choose a file</strong> or drag it here
                                        </span>
                                    </label>
                                </form>
                                <transition name="fade" v-if="form.file != null">
                                    <b-img
                                        v-if="isUploadImage(form.file)"
                                        rounded="bottom"
                                        center
                                        fluid
                                        :src="blobUriSrc"
                                    />
                                    <audio
                                        v-else
                                        style="margin: auto;"
                                        controls="controls"
                                        :src="blobUriSrc"
                                        ref="audio"
                                        id="audio"
                                        :onloaded="audioLoaded()"
                                    >Your browser does not support the HTML5 Audio element.</audio>
                                </transition>
                                <br />
                                <div style="color: #4d535e; font-size: 14px; margin-bottom: 10px">
                                    <em>Drag an image (.png, .jpg, .jpeg, .gif) or audio file (.mp3, .ogg, .wav, .m4a) onto the box above or click it to browse files on your computer.</em>
                                </div>
                                <b-button
                                    block
                                    @click="upload"
                                    :variant="(form.file && !isSaving && form.filename && form.filedescription) ? 'cliovis-blue-light' : 'outline-cliovis-blue-light'"
                                    :disabled="!(form.file && !isSaving && form.filename && form.filedescription)"
                                >
                                    <i class="fas fa-file-upload"></i> Upload
                                </b-button>
                            </div>
                            <div v-if="isSaving">
                                Uploading file {{ form.fileName }}...
                                <b-progress-bar v-model="uploadProgress"></b-progress-bar>
                            </div>
                        </div>
                        <br />
                        <div v-for="(response, ind) in responses" :key="ind">
                            <div v-if="response.status == 'success'">
                                <b-alert dismissible variant="success" show>
                                    <p v-html="response.message" />
                                </b-alert>
                            </div>
                            <div v-else>
                                <b-alert dismissible variant="danger" show>
                                    {{ response.error }}
                                    <b-btn
                                        v-if="!user.isSubscribed && !user.trialing && response.error != null && response.error.includes('Delete')"
                                        style="margin: 3px 5px;"
                                        v-b-tooltip="'Subscribing increases the limit to 100'"
                                        variant="cliovis-blue-light"
                                        size="sm"
                                        @click="$refs.subscriptionModal.show()"
                                        pill
                                    >
                                        <i class="fas fa-plus" /> Increase Limit
                                    </b-btn>
                                </b-alert>
                            </div>
                        </div>
                    </b-form>
                </b-col>
            </b-row>
        </b-container>
        <b-modal
            centered
            ref="subscriptionModal"
            title="Subscribe"
            ok-only
            ok-variant="secondary"
            ok-title="Close"
            size="wide-3"
            no-close-on-backdrop
            no-close-on-esc
        >
            <subscription-panel />
        </b-modal>
    </div>
</template>

<!-- Javascript -->
<script>
import Vue from 'vue'
import { validationMixin } from "vuelidate"
import { required, minLength, between } from 'vuelidate/lib/validators'
import CenteredSpinner from '@/components/CenteredSpinner.vue'
import ProfileView from '@/views/Profile.vue'
import SubscriptionPanel from '@/components/payment/SubscriptionPanel.vue'
import PaymentMethodManager from '@/components/payment/PaymentMethodManager.vue'

export default {
    name: 'Upload',
    components: { CenteredSpinner, SubscriptionPanel, PaymentMethodManager },
    data: function () {
        return {
            isLoading: false,
            dragAndDropCapable: false,
            isInitial: true,
            isSaving: false,
            isFileReady: false,
            blobUriSrc: "",
            uploadProgress: 0,
            form: {},
            responses: [],
            count: { numUploads: 0, maxUploads: 0 }
        }
    },
    mixins:
    {
        validationMixin
    },
    validations: {
        form: {
            filename: {
                required
            },
            filedescription: {
                required
            },
            file: {
                required
            }
        }
    },
    mounted() {
        this.refresh();
        this.$set(this, "form", {})
        setTimeout(() => {
            this.$nextTick(() => {
                this.dragAndDropCapable = this.determineDragAndDropCapable();
                if (this.dragAndDropCapable) {
                    let dragCount = 0;
                    ['uploadForm'].forEach((ref) => {
                        ['dragenter'].forEach((evt) => {
                            this.$refs[ref].addEventListener('dragenter', (e) => {
                                ++dragCount;
                                let root = document.documentElement;
                                root.style.setProperty('--box-bg-color', "white");
                                root.style.setProperty('--box-outline-distance', "-15px");
                            }, false);
                        });

                        ['dragend', 'dragleave', 'drop'].forEach((evt) => {
                            this.$refs[ref].addEventListener(evt, (e) => {
                                --dragCount;
                                dragCount = Math.max(0, dragCount)
                                let root = document.documentElement;
                                if (dragCount == 0) {
                                    root.style.setProperty('--box-bg-color', "rgb(221, 230, 235)");
                                    root.style.setProperty('--box-outline-distance', "-10px");
                                }
                            }, false);
                        });
                    });

                    ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(function (evt) {
                        this.$refs.uploadForm.addEventListener(evt, function (e) {
                            e.preventDefault();
                            e.stopPropagation();
                        }.bind(this), false);
                    }.bind(this));

                    this.$refs.uploadForm.addEventListener('drop', function (e) {
                        this.onChange(e);
                    }.bind(this));
                }
            })
        }, 500);
    },
    methods: {
        async refresh() {
            this.isLoading = true;
            try {
                {
                    let count = await this.$http.get("upload/count");
                    this.$set(this, "count", count.data)
                }
            }
            catch (ex) {
                alert(`Failed to fetch uploads: ${ex}`);
            }
            this.isLoading = false;
        },
        determineDragAndDropCapable() {
            var div = document.createElement('div');
            return (('draggable' in div)
                || ('ondragstart' in div && 'ondrop' in div))
                && 'FormData' in window
                && 'FileReader' in window;
        },

        isUploadImage(upload) {
            if (upload == null || upload.type == null)
                return true;
            return ['image/png', 'image/jpeg', 'image/gif'].includes(upload.type);
        },

        upload() {
            this.responses.length = 0

            if (this.form.file.length > 5 * 1024 * 1024) {
                this.responses.push({
                    status: "failure",
                    message: "Maximum filesize is 5 megabytes"
                })
                return;
            }

            let data = new FormData();
            data.append('file', this.form.file);
            data.append('filename', this.form.filename);
            data.append('filedescription', this.form.filedescription);
            this.isSaving = true;
            this.uploadProgress = 0;

            this.$http.post("upload?" + document.cookie, data, {
                onUploadProgress: (progressEvent) => {
                    let percentCompleted = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
                    this.uploadProgress = percentCompleted
                }
            })
                .then(
                    (response) => {
                        console.log(response);
                        this.$set(this, "form", {});
                        if (response.data.link != null) {
                            response.data.message = `File uploaded successfully!  Click <a href="${response.data.link}" target="_blank">here</a> to see it.`;
                            this.responses.push(response.data);
                        }
                        this.refresh();
                        this.$emit('upload-success');
                    },
                    (error) => {
                        console.log(error.response);
                        let response = error.response;
                        if (response != null && response.data != null && response.data.error != null) {
                            this.responses.push(response.data);
                        } else {
                            this.responses.push({ status: 'failure', error: 'Upload failed! Check if the file is too large (max 5 MB).' });
                        }
                        this.$emit('upload-failed');
                    })
                .finally(() => {
                    this.isSaving = false;
                    if (this.blobUriSrc != null)
                        URL.revokeObjectURL(this.blobUriSrc);
                })
            //data.append('file': )
        },

        onChange(event) {
            let files = event.target.files || event.dataTransfer.files;
            console.log(files);
            console.log(files.length);
            this.$set(this.form, "file", null);
            if (files.length > 0) {
                let file = files[0];
                this.responses.length = 0;
                this.$set(this.form, "file", file);
                if (this.blobUriSrc != null)
                    URL.revokeObjectURL(this.blobUriSrc);
                this.blobUriSrc = URL.createObjectURL(file);
            }
        },

        audioLoaded() {
            this.$nextTick(() => {
                this.$refs.audio.onerror = () => {
                    console.log('bad audio file');
                    this.$set(this.form, "file", null);
                    this.responses.push({ status: 'failure', error: 'Audio file is corrupt or invalid.' });
                }
                this.$refs.audio.onloadedmetadata = () => {
                    console.log(this.$refs.audio.duration);
                }
            });
        },

        navigate(path) {
            p = new ProfileView;
            p.curTab = 1;
            this.$router.push(path);
        }
    }
}
</script>

<!-- SASS styling -->
<style scoped>
:root {
    --box-bg-color: rgb(221, 230, 235);
    --box-outline-distance: -10px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
.upload-input {
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
}

.upload-box {
    display: inline-block;
    line-height: 150px;
    height: 150px;

    width: 100%;
    outline-offset: var(--box-outline-distance);
    outline: 2px dashed black;
    background-color: var(--box-bg-color);
    transition: ease;
    transition-duration: 300ms;
    margin-bottom: 10px;
}

.dragndrop-label {
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    display: block;
    overflow: hidden;
    transition: ease;
    transition-duration: 300ms;
    text-align: center;
}

.dragndrop-label:hover {
    transition: ease;
    transition-duration: 300ms;
    color: rgb(123, 140, 187) !important;
}
</style>
